import React, { useMemo } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ListSidebar, MainLayout } from "layouts";
import styles from "./styles.module.scss";
import { SecurityPage } from "./SecurityPage";
import { ProfilePage } from "./ProfilePage";
import { AppearancePage } from "./AppearancePage";
import { NotificationsPage } from "./NotificationsPage";
import { VoicePage } from "./VoicePage";
import { clsx } from "@ismael1361/utils";
import { Link } from "components";
import { Divider } from "@mui/material";
import { mdiAccountBox, mdiAccountVoice, mdiApi, mdiBell, mdiLock, mdiLogout, mdiPalette, mdiRobot } from "@mdi/js";
import Icon from "@mdi/react";
import { UserServices } from "shared/services";
import { getVersion } from "shared/utils";

interface MenuItemProps {
	icon: string;
	label: string;
	path: string;
	disabled?: boolean;
	isActive?: () => boolean;
	onClick?: () => void;
	color?: string;
}

const menuItems: Array<MenuItemProps> = [
	{ icon: mdiAccountBox, label: "Perfil", path: "/settings", isActive: () => /^\/settings\/?$/gi.test(window.location.pathname) },
	{ icon: mdiLock, label: "Segurança", path: "/settings/security", isActive: () => /^\/settings\/security/gi.test(window.location.pathname) },
	{
		icon: mdiPalette,
		label: "Aparência",
		path: "/settings/appearance",
		isActive: () => /^\/settings\/appearance/gi.test(window.location.pathname),
	},
	{
		icon: mdiAccountVoice,
		label: "Voz",
		path: "/settings/voice",
		isActive: () => /^\/settings\/voice/gi.test(window.location.pathname),
	},
	{ icon: mdiRobot, label: "Preferências de IA", path: "/settings/ai-preferences", disabled: true },
	{ icon: mdiApi, label: "Integrações API", path: "/settings/api-integrations", disabled: true },
	{
		icon: mdiBell,
		label: "Notificações",
		path: "/settings/notifications",
		isActive: () => /^\/settings\/notifications/gi.test(window.location.pathname),
	},
];

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, path, disabled, isActive, onClick, color }) => {
	const location = useLocation();
	return useMemo(
		() => (
			<Link
				href={path}
				disabled={disabled}
				onClick={
					onClick
						? (e) => {
								e.preventDefault();
								onClick();
							}
						: undefined
				}
				className={clsx(styles.navItem, isActive?.() && styles.active)}
				style={{ "--color": color } as React.CSSProperties}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
					<Icon
						path={icon}
						size={1}
					/>
					{label}
				</div>
			</Link>
		),
		[location.pathname, disabled],
	);
};

export const SettingsPage: React.FC = () => {
	return (
		<MainLayout
			sidebar={
				<ListSidebar>
					<aside className={styles.sidebar}>
						<div className={styles.sidebarTitle}>Configurações</div>
						<nav className={styles.nav}>
							{menuItems.map((item, index) => (
								<MenuItem
									key={index}
									{...item}
								/>
							))}
							<Divider />
							<MenuItem
								icon={mdiLogout}
								label="Desconectar Conta"
								path="/"
								onClick={() => {
									UserServices.signOut(true);
								}}
								color="var(--mui-palette-error-main)"
							/>
							<div
								style={{
									height: "auto",
									width: "100%",
									flex: 1,
								}}
							/>
							<div className={styles.versionInfo}>VERSÃO: {getVersion()}</div>
						</nav>
					</aside>
				</ListSidebar>
			}
		>
			<div className={styles.settingsContainer}>
				{/* Main Content Area */}
				<div className={styles.mainContent}>
					<Routes>
						<Route
							path="/"
							element={<ProfilePage />}
						/>
						<Route
							path="/security"
							element={<SecurityPage />}
						/>
						<Route
							path="/appearance"
							element={<AppearancePage />}
						/>
						<Route
							path="/notifications"
							element={<NotificationsPage />}
						/>
						<Route
							path="/voice"
							element={<VoicePage />}
						/>
						<Route
							path="*"
							element={<></>}
						/>
					</Routes>
				</div>
			</div>
		</MainLayout>
	);
};
